/**
 * One PO line's GRN repair board state — shared by the GRN Allocation Repair
 * page and the Import Monitor's ERP-mismatch repair modal.
 *
 * Owns the line detail, the per-schedule allocation slices, drag handling and
 * every manual edit (move, delete, split, and the schedule-level cancel /
 * un-cancel / qty edits). Each successful edit re-fetches the line; edits that
 * change counts elsewhere also call ``onChanged`` so the host can refresh its
 * own list.
 *
 * @param onChanged - called after a move, delete, closest-match rebalance or schedule edit lands
 * @returns detail + loading/error state, slot maps, drag handlers and edit actions
 */
import { useCallback, useMemo, useState } from 'react';
import { PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { toast } from 'sonner';
import { grnRepairAPI, type RepairGRN, type RepairPoLineDetail } from '../../lib/api/grn-repair';
import { fmtDate } from './GrnRepairCards';
import { useGrnScheduleEdits } from './useGrnScheduleEdits';

/** One draggable card: an allocation row, or a legacy GRN with no allocation rows. */
export type RepairSlot = {
  key: string;            // also the dnd-kit drag id
  grn: RepairGRN;
  qty: string;
  isOverDelivery: boolean;
  allocationId: number | null;
};

export function useGrnRepairLine(onChanged?: () => void) {
  const [detail, setDetail] = useState<RepairPoLineDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activeDragGrn, setActiveDragGrn] = useState<RepairGRN | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const fetchDetail = useCallback((id: number) => {
    setDetailLoading(true);
    setDetailError(null);
    grnRepairAPI.getPoLineDetail(id)
      .then((resp) => setDetail(resp.data))
      .catch((err: unknown) => setDetailError(err instanceof Error ? err.message : 'Failed to load PO line detail'))
      .finally(() => setDetailLoading(false));
  }, []);

  // Per-schedule allocation slices. One render entry per
  // GRNScheduleAllocation row, so an over-delivery GRN appears under each
  // schedule it spilled into with the *allocated* qty (not the GRN's full
  // received qty). GRNs with no allocation row + no primary FK go under the
  // "unassigned" pseudo-bucket. Allocation-backed slots carry an
  // ``allocationId`` so per-allocation API calls (move / split) operate on a
  // specific row; legacy slots fall back to the whole-GRN ``link-schedule``.
  /** Close the open line (e.g. when the host switches views). */
  const clearDetail = useCallback(() => {
    setDetail(null);
    setDetailError(null);
  }, []);

  const slotsBySched = useMemo(() => {
    const m = new Map<number | string, RepairSlot[]>();
    if (!detail) return m;
    const grnById = new Map(detail.grns.map((g) => [g.id, g] as const));
    const grnsWithAlloc = new Set<number>();
    for (const a of detail.allocations) {
      const g = grnById.get(a.grn_id);
      if (!g) continue;
      grnsWithAlloc.add(g.id);
      const arr = m.get(a.po_line_schedule_id) || [];
      arr.push({ key: `alloc-${a.id}`, grn: g, qty: a.allocated_qty, isOverDelivery: a.is_over_delivery, allocationId: a.id });
      m.set(a.po_line_schedule_id, arr);
    }
    for (const g of detail.grns) {
      if (grnsWithAlloc.has(g.id)) continue;
      const k = g.po_line_schedule_id ?? 'unassigned';
      const arr = m.get(k) || [];
      arr.push({ key: `grn-${g.id}`, grn: g, qty: g.quantity_received, isOverDelivery: false, allocationId: null });
      m.set(k, arr);
    }
    return m;
  }, [detail]);

  const slotByKey = useMemo(() => {
    const m = new Map<string, RepairSlot>();
    slotsBySched.forEach((arr) => arr.forEach((s) => m.set(s.key, s)));
    return m;
  }, [slotsBySched]);

  const refreshOpenLine = useCallback(() => {
    if (!detail) return;
    fetchDetail(detail.po_line.id);
    onChanged?.();
  }, [detail, fetchDetail, onChanged]);

  const performSlotMove = useCallback(async (slot: RepairSlot, schedId: number) => {
    if (!detail) return;
    const targetSched = detail.schedules.find((s) => s.id === schedId);
    if (!targetSched) return;
    const dest = `line ${targetSched.schedule_line_number}${targetSched.schedule_date ? ` (${fmtDate(targetSched.schedule_date)})` : ' (line 0)'}`;
    try {
      if (slot.allocationId !== null) {
        await grnRepairAPI.moveAllocation(slot.allocationId, schedId);
        toast.success(`GRN #${slot.grn.id} (${slot.qty}) → ${dest}`);
      } else {
        await grnRepairAPI.moveGrn(slot.grn.id, schedId);
        toast.success(`GRN #${slot.grn.id} → ${dest}`);
      }
      refreshOpenLine();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to move');
    }
  }, [detail, refreshOpenLine]);

  const performDelete = useCallback(async (slot: RepairSlot) => {
    if (!detail || slot.allocationId === null) return;
    try {
      await grnRepairAPI.deleteAllocation(slot.allocationId);
      toast.success(`GRN #${slot.grn.id} allocation (${slot.qty}) removed`);
      refreshOpenLine();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }, [detail, refreshOpenLine]);

  // Closest-match auto-rebalance for the whole line (GRN Repair's toolbar action).
  const [rebalancing, setRebalancing] = useState<null | 'closest'>(null);
  const performRebalance = useCallback(async () => {
    if (!detail) return;
    setRebalancing('closest');
    try {
      const resp = await grnRepairAPI.rebalance(detail.po_line.id);
      toast.success(`${resp.data.moved} GRN(s) rebalanced (closest-match).`);
      refreshOpenLine();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Rebalance failed');
    } finally {
      setRebalancing(null);
    }
  }, [detail, refreshOpenLine]);

  const [splitTarget, setSplitTarget] = useState<RepairSlot | null>(null);
  const [splitQty, setSplitQty] = useState('');
  const performSplit = useCallback(async () => {
    if (!detail || !splitTarget || splitTarget.allocationId === null) return;
    const qty = splitQty.trim();
    if (!qty) { toast.error('Enter a split quantity'); return; }
    try {
      await grnRepairAPI.splitAllocation(splitTarget.allocationId, qty);
      toast.success(`GRN #${splitTarget.grn.id} split: ${qty} chipped off`);
      setSplitTarget(null);
      setSplitQty('');
      fetchDetail(detail.po_line.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Split failed');
    }
  }, [detail, splitTarget, splitQty, fetchDetail]);

  const scheduleEdits = useGrnScheduleEdits(detail?.po_line.id ?? null, refreshOpenLine);

  const onDragStart = (e: DragStartEvent) => {
    if (!detail) return;
    setActiveDragGrn(slotByKey.get(String(e.active.id))?.grn ?? null);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveDragGrn(null);
    if (!e.over || !detail) return;
    const slot = slotByKey.get(String(e.active.id));
    if (!slot) return;
    performSlotMove(slot, Number(String(e.over.id).replace('sched-', '')));
  };

  return {
    detail, detailLoading, detailError, fetchDetail, clearDetail, sensors,
    refreshOpenLine,
    slotsBySched, activeDragGrn, onDragStart, onDragEnd,
    performSlotMove, performDelete, rebalancing, performRebalance,
    splitTarget, setSplitTarget, splitQty, setSplitQty, performSplit,
    ...scheduleEdits,
  };
}

export type GrnRepairLine = ReturnType<typeof useGrnRepairLine>;
